import { useEffect, useLayoutEffect, useRef } from 'react';

export const useIsomorphicLayoutEffect = typeof window !== 'undefined'
  ? useLayoutEffect
  : useEffect;

export function useInterval(callback: () => void, delay: number | null): void {
  const savedCallback = useRef(callback);

  // Remember the latest callback if it changes.
  useIsomorphicLayoutEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    // Don't schedule if no delay is specified.
    // Note: 0 is a valid value for delay.
    if (!delay && delay !== 0) {
      return;
    }

    const id = setInterval(() => savedCallback.current(), delay);

    // eslint-disable-next-line consistent-return
    return (): void => clearInterval(id);
  }, [delay]);
}

export const structuredClone = (
  obj: object | unknown[],
): any => JSON.parse(JSON.stringify(obj));

type AnyObject = { [key: string]: any };

const toCamelCase = (str: string): string => str.replace(
  /([-_][a-z])/g,
  (group) => group.toUpperCase()
    .replace('-', '')
    .replace('_', ''),
);

const replaceIDfromSnakeCase = (str: string): string => str
  .replace(/_id|id_|_id_/g, 'ID');

export const toCamelCaseDeep = <T = any>(value: any): T => {
  if (typeof value === 'object' && value !== null) {
    if (Array.isArray(value)) {
      return value.map(toCamelCaseDeep) as unknown as T;
    }
    const newObj: AnyObject = {};

    Object.entries(value).forEach(([key, nestedValue]) => {
      const camelCasedKey = toCamelCase(replaceIDfromSnakeCase(key));
      newObj[camelCasedKey] = toCamelCaseDeep(nestedValue);
    });

    return newObj as T;
  }
  return value as T;
};

function camelToSnakeCase(str: string): string {
  return str
    .replace(/^ID(?=[A-Z])/g, 'id_')
    .replace(/ID/g, '_id')
    .replace(/(?<=[a-z])(?=[A-Z])/g, '_')
    .toLowerCase();
}

export const toSnakeCaseDeep = <T = any>(value: T): T => {
  if (Array.isArray(value)) {
    return value.map(toSnakeCaseDeep) as unknown as T;
  } if (value !== null && typeof value === 'object') {
    return Object.keys(value).reduce((prev: T, key: string) => {
      const newKey = camelToSnakeCase(key);
      const result = prev;
      (result as any)[newKey] = toSnakeCaseDeep((value as any)[key]);
      return result;
    }, {} as T);
  }
  return value;
};
